import React from 'react';
import {
  Card, CardHeader, Stack, CardProps as MuiCardProps, Skeleton, IconButton, CardContent, Link, Typography,
} from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import { ConversationSummary, ConversationType } from '@marageti/z4-sdk/lib/travel';
import ConversationLinks from '../conversation-links';

type CardProps = {
  avatar: React.ReactNode,
  title: React.ReactNode,
  subheader: React.ReactNode,
  actions: React.ReactNode[],
  variant?: MuiCardProps['variant'],
  sx?: MuiCardProps['sx'],
  tripRequestId?: string,
  travelerId: string,
  id: string, // tripMatchId,
};

const MatchCard = ({
  avatar,
  title,
  subheader,
  actions,
  variant = 'elevation',
  sx = {},
  tripRequestId,
  travelerId,
  id,
}: CardProps) => {
  const renderConversationLink = ({
    conversationSummary,
  }: {
    conversationSummary: ConversationSummary,
  }) => {
    switch (conversationSummary.conversationType) {
      case ConversationType.ZicassoToPartner:
        return `/trip-matches/${id}/messages/${conversationSummary.conversationId}`;
      case ConversationType.TravelerToPartner:
        return `/trip-matches/${id}/messages/${conversationSummary.conversationId}?viewerId=${travelerId}`;
      default:
        return '';
    }
  };

  const getConversationTypeTitle = (
    conversationType: ConversationType,
  ): any => {
    switch (conversationType) {
      case ConversationType.TravelerToPartner:
        return <Typography variant="bodyBold" sx={{ '&:hover': { textDecoration: 'underline' } }}>Partner & Traveler</Typography>;
      case ConversationType.ZicassoToPartner:
        return <Typography variant="bodyBold" sx={{ '&:hover': { textDecoration: 'underline' } }}>Partner & Zicasso</Typography>;
      case ConversationType.TravelerToZicasso:
        return 'Zicasso Care';
      default:
        return 'Unknown Conversation Type';
    }
  };

  return (
    <Card
      variant={variant}
      sx={sx}
    >
      <CardHeader
        avatar={avatar}
        sx={{
          backgroundColor: 'neutral.main',
          '.MuiCardHeader-avatar': {
            marginRight: '16px',
          },
          '.MuiCardHeader-content': {
            overflow: 'hidden',
            marginRight: '24px',
          },
          '.MuiCardHeader-action': {
            alignSelf: 'center',
          },
          pb: 3,
        }}
        title={(
          <Link
            href={`/trip-matches/${id}`}
            component="a"
            sx={{
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {title}
          </Link>
)}
        titleTypographyProps={{
          variant: 'bodyBold',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        }}
        subheader={subheader}
        action={(
          <Stack
            direction="row"
            height="100%"
            alignItems="center"
            spacing={1}
          >
            {actions.map((action: any, index: number) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index}>{action}</div>
            ))}
          </Stack>
        )}
      />
      <CardContent>
        {tripRequestId && (
          <ConversationLinks
            tripRequestId={tripRequestId}
            tripMatchId={id}
            renderConversationLink={renderConversationLink}
            getConversationTypeTitle={getConversationTypeTitle}
          />
        )}
      </CardContent>
    </Card>
  );
};

MatchCard.Skeleton = () => (
  <MatchCard
    avatar={<Skeleton variant="circular" width={24} height={24} />}
    title={<Skeleton variant="text" width={100} />}
    subheader={<Skeleton variant="text" width={100} />}
    actions={[
      <Skeleton variant="text" width={50} />,
      <IconButton disabled>
        <OutlinedIcons.OutlinedDotsVerticalIcon color="primary" />
      </IconButton>,
    ]}
    tripRequestId=""
    travelerId=""
    id=""
  />
);

export default MatchCard;
